import React from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { RepairOption } from '../types'

interface ExpressRepairModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (withExpress: boolean) => void
  repair: RepairOption
}

const ExpressRepairModal: React.FC<ExpressRepairModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  repair
}) => {
  if (!isOpen) return null

  const expressPrice = repair.expressSurcharge
  const totalWithExpress = repair.price + expressPrice

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-gray-900">Express-Service</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              24h Express-Reparatur
            </h3>
            <p className="text-gray-600 text-sm">
              Deine Reparatur wird prioritär bearbeitet und ist bereits nach 24 Stunden fertig!
            </p>
          </div>

          {/* Price Comparison */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-700">Standard-Reparatur:</span>
              <span className="font-medium">{repair.price.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between items-center text-orange-600">
              <span className="font-medium">+ Express-Service:</span>
              <span className="font-bold">+{expressPrice.toFixed(2)} €</span>
            </div>
            <div className="border-t pt-2 flex justify-between items-center">
              <span className="font-semibold text-gray-900">Gesamt:</span>
              <span className="font-bold text-lg text-orange-600">{totalWithExpress.toFixed(2)} €</span>
            </div>
          </div>

          {/* Benefits */}
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Deine Vorteile:</h4>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Prioritäre Bearbeitung deiner Reparatur</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Fertigstellung innerhalb von 24 Stunden</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Express-Rückversand am gleichen Tag</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span className="text-green-500">✓</span>
                <span>Email-Benachrichtigung bei Statusänderung</span>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              💡 <strong>Tipp:</strong> Der Express-Service ist ideal, wenn du dein Gerät schnell zurück brauchst!
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={() => onConfirm(true)}
            className="w-full bg-orange-500 text-white px-4 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors flex items-center justify-center gap-2"
          >
            Express hinzufügen (+{expressPrice.toFixed(2)} €)
          </button>
          <button
            onClick={() => onConfirm(false)}
            className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-700 transition-colors"
          >
            Nein, Standard-Reparatur (1-2 Tage)
          </button>
        </div>
      </div>
    </div>
  )
}

export default ExpressRepairModal